import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { translations } from './translations';
import './DropdownMenu.css';
import MenuIcon from './Smenu6.svg';
import MenuImage from './menu-image.png';

function DropdownMenu() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );
  const { language, setLanguage } = useLanguage();
  const t = translations[language];
  const dropdownRef = useRef(null);

  const navItems = [
    { to: '/', label: t.home },
    { to: '/about', label: t.about },
    { to: '/procedures', label: t.procedures },
    { to: '/surgeries', label: t.surgeries },
  ];

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false);
    document.body.style.overflow = '';
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => {
      const nextValue = !prev;

      if (isMobile) {
        document.body.style.overflow = nextValue ? 'hidden' : '';
      }

      return nextValue;
    });
  };

  useEffect(() => {
    const handleResize = () => {
      const nextIsMobile = window.innerWidth <= 768;
      setIsMobile(nextIsMobile);
      setIsDropdownOpen(false);
      document.body.style.overflow = '';
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.body.style.overflow = '';
    };
  }, [closeDropdown, isMobile]);

  const renderLanguageSwitcher = (className = '') => (
    <div className={`language-switcher ${className}`.trim()}>
      <span className={language === 'EN' ? 'active-lang' : ''} onClick={() => setLanguage('EN')}>EN</span>
      <span className="lang-separator">|</span>
      <span className={language === 'ES' ? 'active-lang' : ''} onClick={() => setLanguage('ES')}>ES</span>
      <span className="lang-separator">|</span>
      <span className={language === 'PT' ? 'active-lang' : ''} onClick={() => setLanguage('PT')}>PT</span>
    </div>
  );

  const renderDesktopMenu = () => (
    <div className="dropdown-content dropdown-desktop-content">
      <nav className="dropdown-desktop-nav">
        {navItems.map((item) => (
          <Link key={item.to} to={item.to} className="dropdown-link" onClick={closeDropdown}>
            {item.label}
          </Link>
        ))}
        {renderLanguageSwitcher('dropdown-desktop-language-switcher')}
      </nav>
    </div>
  );

  const mobileDropdown = isDropdownOpen && isMobile ? createPortal(
    <div className="dropdown-overlay" onClick={closeDropdown}>
      <div className="dropdown-mobile-panel" onClick={(event) => event.stopPropagation()}>
        <div className="dropdown-mobile-header">
          <span className="dropdown-mobile-kicker">Menu</span>
          <button
            className="close-btn"
            onClick={closeDropdown}
            type="button"
            aria-label="Close menu"
          >
            &times;
          </button>
        </div>
        <nav className="dropdown-mobile-nav">
          {navItems.map((item) => (
            <Link key={item.to} to={item.to} className="dropdown-link dropdown-mobile-link" onClick={closeDropdown}>
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="dropdown-mobile-footer">
          {renderLanguageSwitcher('dropdown-mobile-language-switcher')}
          <img src={MenuImage} className="menu-image" alt="Menu visual" />
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div
      className="dropdown-container"
      ref={dropdownRef}
    >
      <img
        src={MenuIcon}
        className="dropdown-logo"
        alt="Menu"
        onClick={toggleDropdown}
      />
      {isDropdownOpen && !isMobile && renderDesktopMenu()}
      {mobileDropdown}
    </div>
  );
}

export default DropdownMenu;
